import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { Address } from './address';
import { BaseModel } from './base-model';
import { ExemptVendor } from './exempt-vendor';
import { Proposal } from './proposal';
import { ProposalRequest } from './proposal-request';
import { Vendor } from './vendor';

export class ProposalRequestVendor extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static ProposalRequestVendorProperties: (
    | {
        property: keyof ProposalRequestVendor;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof ProposalRequestVendor;
        type: 'alias';
      }
    | {
        property: keyof ProposalRequestVendor;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'ProposalRequestId', type: 'column', dataType: 'number' },
    { property: 'VendorId', type: 'column', dataType: 'number' },
    { property: 'ExemptVendorId', type: 'column', dataType: 'number' },
    { property: 'InvitationContactId', type: 'column', dataType: 'number' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'firstName', type: 'column', dataType: 'string' },
    { property: 'lastName', type: 'column', dataType: 'string' },
    { property: 'phoneExt', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'phone2Ext', type: 'column', dataType: 'string' },
    { property: 'phone2', type: 'column', dataType: 'string' },
    { property: 'email', type: 'column', dataType: 'string' },
    { property: 'SubNationalDivisionId', type: 'column', dataType: 'number' },
    { property: 'acceptsRequest', type: 'column', dataType: 'boolean' },
    { property: 'isAwarded', type: 'column', dataType: 'boolean' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'declinedDate', type: 'column', dataType: 'date' },
    { property: 'isSelectedForReport', type: 'column', dataType: 'boolean' },

    { property: 'bidStatus', type: 'alias' },
    { property: 'totalAmount', type: 'alias' },
    { property: 'clientSpecificVendorRating', type: 'alias' },
    { property: 'spotlightVendorPriorityLevel', type: 'alias' },

    {
      property: 'ProposalRequest',
      model: 'ProposalRequest',
      type: 'association'
    },
    { property: 'Vendor', model: 'Vendor', type: 'association' },
    { property: 'ExemptVendor', model: 'ExemptVendor', type: 'association' },
    {
      property: 'SubNationalDivision',
      model: 'SubNationalDivision',
      type: 'association'
    },
    { property: 'Proposal', model: 'Proposal', type: 'association' },
    { property: 'Address', model: 'Address', type: 'association' }
  ];

  static {
    ProposalRequestVendor.ProposalRequestVendorProperties.forEach(
      (propertyObject) => {
        const { property, type } = propertyObject;
        if (type === 'column') {
          ProposalRequestVendor.columnMap.set(property, propertyObject);
        }
        if (type === 'alias') {
          ProposalRequestVendor.aliasedColumnMap.set(
            property,
            propertyObject as { property: string; type: 'alias' }
          );
        }
        if (type === 'association') {
          ProposalRequestVendor.associationMap.set(
            property,
            propertyObject as {
              property: string;
              type: 'association';
              model: string;
            }
          );
        }
      }
    );
  }

  constructor(ProposalRequestVendorObject: Partial<ProposalRequestVendor>) {
    super();

    ProposalRequestVendor.ProposalRequestVendorProperties.forEach(
      (propertyObject) => {
        const { property } = propertyObject;
        const value = ProposalRequestVendorObject[property];
        if (value !== undefined) {
          Object.assign(this, { [property]: value });
        }
      }
    );
  }

  id?: number;
  ProposalRequestId?: number;
  VendorId?: number | null;
  ExemptVendorId?: number | null;
  InvitationContactId?: number;
  dba?: string | null;
  legalName?: string;
  firstName?: string;
  lastName?: string;
  phoneExt?: string | null;
  phone?: string;
  phone2Ext?: string | null;
  phone2?: string | null;
  email?: string | null;
  SubNationalDivisionId?: number;
  acceptsRequest?: boolean | null;
  isAwarded?: boolean;
  status?:
    'CREATED' | 'ALERTED' | 'AWARDED' | 'INVITED' | 'NOTIFIED' | 'REJECTED';
  statusDate?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  declinedDate?: Date;
  isSelectedForReport?: boolean;

  // Alias column(s)
  bidStatus?: 'BID SUBMITTED' | 'BID NOT SUBMITTED';
  // This represents either the totalPrice or the totalled price of all line item amounts.
  totalAmount?: number;
  clientSpecificVendorRating?: number;
  spotlightVendorPriorityLevel?: number;

  ProposalRequest?: ProposalRequest;
  Vendor?: Vendor;
  ExemptVendor?: ExemptVendor;
  SubNationalDivision?: SubNationalDivision;
  Proposal?: Proposal;
  Address?: Address;
}

/*
  ProposalRequestVendor.associate = (models) => {
    ProposalRequestVendor.belongsTo(models.InvitationContact);
  };
*/
